import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, TrendingDown, DollarSign, Wallet, 
  ShoppingCart, AlertCircle, CheckCircle, RefreshCw,
  ArrowUpRight, ArrowDownRight, Activity, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function BrokerTradingPage() {
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const [brokerStatus, setBrokerStatus] = useState(null);
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderLoading, setOrderLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Order form state
  const [orderForm, setOrderForm] = useState({
    symbol: '',
    qty: '',
    side: 'buy',
    orderType: 'market',
    limitPrice: '',
    timeInForce: 'day'
  });

  useEffect(() => {
    fetchBrokerData();
  }, []);

  const fetchBrokerData = async () => {
    setLoading(true);
    try {
      // Check broker status
      const statusRes = await api.get('/broker/status');
      setBrokerStatus(statusRes.data);
      
      if (statusRes.data.connected) {
        // Fetch account, positions, and orders in parallel
        const [accountRes, positionsRes, ordersRes] = await Promise.all([
          api.get('/broker/account').catch(() => ({ data: null })),
          api.get('/broker/positions').catch(() => ({ data: { positions: [] } })),
          api.get('/broker/orders?limit=20').catch(() => ({ data: { orders: [] } }))
        ]);
        
        setAccount(accountRes.data);
        setPositions(positionsRes.data?.positions || []);
        setOrders(ordersRes.data?.orders || []);
      }
    } catch (error) {
      console.error('Failed to fetch broker data:', error);
      toast.error('Failed to connect to broker');
    } finally {
      setLoading(false);
    }
  };

  const syncPortfolio = async () => {
    setSyncing(true);
    try {
      const res = await api.get('/broker/portfolio/sync');
      setAccount(res.data.account);
      setPositions(res.data.positions);
      toast.success('Portfolio synced successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync portfolio');
    } finally {
      setSyncing(false);
    }
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    
    if (!orderForm.symbol || !orderForm.qty) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (orderForm.orderType === 'limit' && !orderForm.limitPrice) {
      toast.error('Limit price required for limit orders');
      return;
    }

    setOrderLoading(true);
    try {
      const orderData = {
        symbol: orderForm.symbol.toUpperCase(),
        qty: parseFloat(orderForm.qty),
        side: orderForm.side,
        order_type: orderForm.orderType,
        time_in_force: orderForm.timeInForce,
        ...(orderForm.orderType === 'limit' && { limit_price: parseFloat(orderForm.limitPrice) })
      };

      const res = await api.post('/broker/order', orderData);
      
      toast.success(`${orderForm.side.toUpperCase()} order placed for ${orderForm.qty} ${orderForm.symbol}`);
      
      // Reset form
      setOrderForm(prev => ({ ...prev, symbol: '', qty: '', limitPrice: '' }));
      
      // Refresh data
      fetchBrokerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setOrderLoading(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await api.delete(`/broker/orders/${orderId}`);
      toast.success('Order cancelled');
      fetchBrokerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    }
  };

  const handleClosePosition = async (symbol) => {
    try {
      await api.delete(`/broker/positions/${symbol}`);
      toast.success(`Position closed for ${symbol}`);
      fetchBrokerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close position');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-[#00E676] animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Connecting to broker...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!brokerStatus?.connected) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto">
          <Card className="card-dashboard border-yellow-500/50">
            <CardContent className="py-8 text-center">
              <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Broker Not Configured</h2>
              <p className="text-gray-400 mb-6">
                To enable real trading, you need to configure your Alpaca API keys.
              </p>
              <div className="bg-white/5 rounded-xl p-6 text-left space-y-4">
                <h3 className="font-semibold text-white">Setup Instructions:</h3>
                <ol className="list-decimal list-inside text-gray-400 space-y-2">
                  <li>Create a free account at <a href="https://alpaca.markets" target="_blank" rel="noopener noreferrer" className="text-[#00E676] hover:underline">alpaca.markets</a></li>
                  <li>Go to your Alpaca Dashboard and generate Paper Trading API keys</li>
                  <li>Provide your ALPACA_API_KEY and ALPACA_SECRET_KEY</li>
                  <li>Refresh this page to start trading</li>
                </ol>
                <p className="text-sm text-gray-500 mt-4">
                  Paper trading uses simulated money - no risk to real funds!
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="broker-trading-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Real Trading
            </h1>
            <p className="text-gray-400 mt-1 flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${brokerStatus?.mode === 'paper' ? 'bg-yellow-500' : 'bg-green-500'}`} />
              {brokerStatus?.mode === 'paper' ? 'Paper Trading Mode' : 'Live Trading'}
            </p>
          </div>
          <Button 
            onClick={syncPortfolio} 
            disabled={syncing}
            variant="outline"
            className="btn-secondary"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Portfolio'}
          </Button>
        </div>

        {/* Account Overview */}
        {account && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-dashboard">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-8 h-8 text-[#00E676]" />
                  <div>
                    <p className="text-xs text-gray-400">Portfolio Value</p>
                    <p className="text-xl font-bold text-white number">{formatCurrency(account.portfolio_value)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-dashboard">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <Wallet className="w-8 h-8 text-blue-400" />
                  <div>
                    <p className="text-xs text-gray-400">Cash</p>
                    <p className="text-xl font-bold text-white number">{formatCurrency(account.cash)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-dashboard">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <ShoppingCart className="w-8 h-8 text-purple-400" />
                  <div>
                    <p className="text-xs text-gray-400">Buying Power</p>
                    <p className="text-xl font-bold text-white number">{formatCurrency(account.buying_power)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-dashboard">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <Activity className="w-8 h-8 text-orange-400" />
                  <div>
                    <p className="text-xs text-gray-400">Equity</p>
                    <p className="text-xl font-bold text-white number">{formatCurrency(account.equity)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="trade" className="w-full">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="trade" className="data-[state=active]:bg-[#00E676] data-[state=active]:text-black">Place Order</TabsTrigger>
            <TabsTrigger value="positions" className="data-[state=active]:bg-[#00E676] data-[state=active]:text-black">Positions ({positions.length})</TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#00E676] data-[state=active]:text-black">Orders ({orders.length})</TabsTrigger>
          </TabsList>

          {/* Place Order Tab */}
          <TabsContent value="trade">
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white">Place Trade Order</CardTitle>
                <CardDescription>Buy or sell stocks and crypto through Alpaca</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePlaceOrder} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Side</label>
                      <Select 
                        value={orderForm.side} 
                        onValueChange={(v) => setOrderForm(prev => ({ ...prev, side: v }))}
                      >
                        <SelectTrigger className="input-dark">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="buy">
                            <span className="flex items-center gap-2">
                              <ArrowUpRight className="w-4 h-4 text-green-500" /> Buy
                            </span>
                          </SelectItem>
                          <SelectItem value="sell">
                            <span className="flex items-center gap-2">
                              <ArrowDownRight className="w-4 h-4 text-red-500" /> Sell
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Order Type</label>
                      <Select 
                        value={orderForm.orderType} 
                        onValueChange={(v) => setOrderForm(prev => ({ ...prev, orderType: v }))}
                      >
                        <SelectTrigger className="input-dark">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="market">Market</SelectItem>
                          <SelectItem value="limit">Limit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm text-gray-400">Symbol</label>
                    <Input
                      placeholder="AAPL, GOOGL, BTC/USD..."
                      value={orderForm.symbol}
                      onChange={(e) => setOrderForm(prev => ({ ...prev, symbol: e.target.value.toUpperCase() }))}
                      className="input-dark"
                      required
                      data-testid="order-symbol"
                    />
                    <p className="text-xs text-gray-500">For crypto, use format: BTC/USD, ETH/USD</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Quantity</label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="1.5"
                        value={orderForm.qty}
                        onChange={(e) => setOrderForm(prev => ({ ...prev, qty: e.target.value }))}
                        className="input-dark"
                        required
                        data-testid="order-qty"
                      />
                    </div>

                    {orderForm.orderType === 'limit' && (
                      <div className="space-y-2">
                        <label className="text-sm text-gray-400">Limit Price</label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="150.50"
                          value={orderForm.limitPrice}
                          onChange={(e) => setOrderForm(prev => ({ ...prev, limitPrice: e.target.value }))}
                          className="input-dark"
                          required
                          data-testid="order-limit-price"
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Time in Force</label>
                      <Select 
                        value={orderForm.timeInForce} 
                        onValueChange={(v) => setOrderForm(prev => ({ ...prev, timeInForce: v }))}
                      >
                        <SelectTrigger className="input-dark">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="day">Day</SelectItem>
                          <SelectItem value="gtc">Good Till Cancelled</SelectItem>
                          <SelectItem value="ioc">Immediate or Cancel</SelectItem>
                          <SelectItem value="fok">Fill or Kill</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    disabled={orderLoading}
                    className={`w-full h-12 ${orderForm.side === 'buy' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
                    data-testid="place-order-btn"
                  >
                    {orderLoading ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        {orderForm.side === 'buy' ? <ArrowUpRight className="w-5 h-5 mr-2" /> : <ArrowDownRight className="w-5 h-5 mr-2" />}
                        {orderForm.side === 'buy' ? 'Buy' : 'Sell'} {orderForm.symbol || 'Asset'}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Positions Tab */}
          <TabsContent value="positions">
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white">Open Positions</CardTitle>
                <CardDescription>Your current holdings</CardDescription>
              </CardHeader>
              <CardContent>
                {positions.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No open positions</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 text-gray-400">Symbol</th>
                          <th className="text-right text-gray-400">Qty</th>
                          <th className="text-right text-gray-400">Avg Price</th>
                          <th className="text-right text-gray-400">Current</th>
                          <th className="text-right text-gray-400">Market Value</th>
                          <th className="text-right text-gray-400">P&L</th>
                          <th className="text-right text-gray-400">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {positions.map((pos) => (
                          <tr key={pos.symbol} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 font-medium text-white">{pos.symbol}</td>
                            <td className="text-right text-white number">{pos.qty.toFixed(2)}</td>
                            <td className="text-right text-gray-400 number">{formatCurrency(pos.avg_entry_price)}</td>
                            <td className="text-right text-white number">{formatCurrency(pos.current_price)}</td>
                            <td className="text-right text-white number">{formatCurrency(pos.market_value)}</td>
                            <td className={`text-right font-medium number ${pos.unrealized_pl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {pos.unrealized_pl >= 0 ? '+' : ''}{formatCurrency(pos.unrealized_pl)}
                              <span className="text-xs ml-1">({pos.unrealized_pl_pct.toFixed(2)}%)</span>
                            </td>
                            <td className="text-right">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleClosePosition(pos.symbol)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                Close
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white">Order History</CardTitle>
                <CardDescription>Recent orders and their status</CardDescription>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No orders yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 text-gray-400">Symbol</th>
                          <th className="text-center text-gray-400">Side</th>
                          <th className="text-right text-gray-400">Qty</th>
                          <th className="text-right text-gray-400">Type</th>
                          <th className="text-center text-gray-400">Status</th>
                          <th className="text-right text-gray-400">Filled Avg</th>
                          <th className="text-right text-gray-400">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                          <tr key={order.order_id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 font-medium text-white">{order.symbol}</td>
                            <td className="text-center">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                order.side === 'buy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                              }`}>
                                {order.side?.toUpperCase()}
                              </span>
                            </td>
                            <td className="text-right text-white number">{order.qty}</td>
                            <td className="text-right text-gray-400">{order.type}</td>
                            <td className="text-center">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                order.status === 'filled' ? 'bg-green-500/20 text-green-400' :
                                order.status === 'cancelled' ? 'bg-gray-500/20 text-gray-400' :
                                order.status === 'pending_new' || order.status === 'new' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-blue-500/20 text-blue-400'
                              }`}>
                                {order.status}
                              </span>
                            </td>
                            <td className="text-right text-gray-400 number">
                              {order.filled_avg_price ? formatCurrency(order.filled_avg_price) : '-'}
                            </td>
                            <td className="text-right">
                              {['pending_new', 'new', 'accepted', 'partially_filled'].includes(order.status) && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleCancelOrder(order.order_id)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
